import { requireService, wrap } from '@repo/di-ez';
import { Button, Collapse, Modal, Radio, Scrollbar } from '@repo/go-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WellsTableTemplateStore } from '../wells-table-template.store';
import type { TWellTableTemplate, TWellTableTemplateData } from '../wells-table-templates.api';

import styles from './export-template-modal.module.scss';

type Props = {
  templatesStore: WellsTableTemplateStore;
  onExportTemplates: (data?: TWellTableTemplateData | 'currentTableSettings') => void;
};

const TemplateItem = wrap<{ template: TWellTableTemplate; onSelect: VoidFunction; isSelected: boolean }>(
  function TemplateItem({ template, onSelect, isSelected }) {
    return (
      <div className={styles.templateItem}>
        <Radio id={template.id} onChange={onSelect} checked={isSelected} />
        <label htmlFor={template.id} className={styles.templateItemLabel}>
          <p>{template.name}</p>
          <p className={styles.templateItemDescription}>{template.description}</p>
        </label>
      </div>
    );
  },
);

export const ExportTemplatesModal = wrap<Props>(function ExportTemplatesModal({ templatesStore, onExportTemplates }) {
  const { t } = useTranslation();
  const pageStore = requireService('wellsPageStore');
  const [activeKeys, setActiveKeys] = useState<string[]>(['private']);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | 'currentTableSettings'>('currentTableSettings');
  const { isExportTemplatesOpen, setIsExportTemplatesOpen } = pageStore;
  const { templatesMap } = templatesStore;

  const clearState = useCallback(() => {
    setTimeout(() => {
      setSelectedTemplateId('currentTableSettings');
      setActiveKeys(['private']);
    }, 400);
  }, []);

  const handleExport = () => {
    if (!selectedTemplateId) {
      return;
    }

    const selectedTemplate = templatesMap.get(selectedTemplateId);

    if (!selectedTemplate && selectedTemplateId !== 'currentTableSettings') {
      console.error('selected template is not found');
      return;
    }

    onExportTemplates(selectedTemplate?.data ?? 'currentTableSettings');
    clearState();
  };

  return (
    <Modal
      open={isExportTemplatesOpen}
      title={<h3 className="h3">{t('wells:Templates.tableSettingsTemplates')}</h3>}
      onClose={() => pageStore.setIsExportTemplatesOpen(false)}
      onClickOutside={null}
    >
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <Radio
            id="currentTableSettings"
            className={styles.currentTableSettingsRadio}
            checked={selectedTemplateId === 'currentTableSettings'}
            onChange={() => setSelectedTemplateId('currentTableSettings')}
          >
            {t('wells:Templates.useCurrentTableSettings')}
          </Radio>
          <div className={styles.templatesWrapper}>
            <Scrollbar style={{ flexGrow: 1 }}>
              <Collapse activeKeys={activeKeys} onChange={(v) => setActiveKeys(v.map((key) => key.toString()))}>
                <Collapse.Panel
                  panelKey="system"
                  styleHeader={{ height: 40 }}
                  className={styles.templatePanel}
                  header={t('wells:Templates.publicTemplates')}
                >
                  <div className={styles.templateItems}>
                    {templatesStore.templates.system.map((template) => {
                      return (
                        <TemplateItem
                          key={template.id}
                          template={template}
                          isSelected={selectedTemplateId === template.id}
                          onSelect={() => {
                            setSelectedTemplateId(template.id);
                          }}
                        />
                      );
                    })}
                  </div>
                </Collapse.Panel>
                <Collapse.Panel
                  panelKey="private"
                  styleHeader={{ height: 40 }}
                  className={styles.templatePanel}
                  header={t('wells:Templates.privateTemplates')}
                >
                  <div className={styles.templateItems}>
                    {templatesStore.templates.private.map((template) => (
                      <TemplateItem
                        key={template.id}
                        isSelected={selectedTemplateId === template.id}
                        template={template}
                        onSelect={() => {
                          setSelectedTemplateId(template.id);
                        }}
                      />
                    ))}
                  </div>
                </Collapse.Panel>
              </Collapse>
            </Scrollbar>
          </div>
        </div>

        <div className={styles.buttons}>
          <Button
            variant="secondary"
            className={styles.button}
            onClick={() => {
              setIsExportTemplatesOpen(false);
              clearState();
            }}
          >
            {t('common:Buttons.cancel')}
          </Button>
          <Button
            variant="success"
            className={styles.button}
            disabled={!selectedTemplateId}
            onClick={() => {
              handleExport();
              setIsExportTemplatesOpen(false);
              clearState();
            }}
          >
            {t('common:export')}
          </Button>
        </div>
      </div>
    </Modal>
  );
});
